import React, { useEffect, ChangeEvent } from 'react';
import { Form } from 'react-bootstrap';
import { InputFieldState } from '../types';
import { LOCAL_CONSTANT } from '../localConstant';

/**
 * RepresentativeFormコンポーネント用のPropsです。
 * @interface RepresentativeFormProps
 */
interface RepresentativeFormProps {
  /**
   * 代表者
   * @type {InputFieldState}
   * @memberof RepresentativeFormProps
   */
  representative: InputFieldState;

  /**
   * 氏名又は名称
   * @type {InputFieldState}
   * @memberof RepresentativeFormProps
   */
  name: InputFieldState;

  /**
   * 手続実行者フラグ
   * @type {boolean}
   * @memberof RepresentativeFormProps
   */
  isProcedureExecutor: boolean;

  /**
   * 代表者を設定する関数
   * @memberof RepresentativeFormProps
   */
  onChange: (representative: InputFieldState) => void;
}

/**
 * 代表者フォームのコンポーネントです。
 * @param {string} props.representative - 表示する代表者
 * @param {function} props.onChange - 代表者が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 代表者フォームのJSX要素
 */
const RepresentativeForm: React.FC<RepresentativeFormProps> = ({ representative, name, isProcedureExecutor, onChange }) => {

  /**
   * フォームの変更イベントハンドラーです。
   * 入力された代表者をonChange関数を呼び出します。
   * @param {object} event - フォームの変更イベント
   * @returns {void}
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    const isIncludingLegalPersonality = LOCAL_CONSTANT.LEGAL_PERSONALITIES.some(legalPersonality => name.value.indexOf(legalPersonality) !== -1);
    onChange({
      value: halfWidthToFullWidth(value),
      errorMessage: isProcedureExecutor && isIncludingLegalPersonality === true && value === '' ? '代表者が入力されていません。法人の場合は、代表者を入力する必要があります。' : ''
    });
  };

  useEffect(() => {
    const isIncludingLegalPersonality = LOCAL_CONSTANT.LEGAL_PERSONALITIES.some(legalPersonality => name.value.indexOf(legalPersonality) !== -1);
    onChange({
      value: halfWidthToFullWidth(representative.value),
      errorMessage: isProcedureExecutor && isIncludingLegalPersonality === true && representative.value === '' ? '代表者が入力されていません。法人の場合は、代表者を入力する必要があります。' : ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name.value, isProcedureExecutor, representative.value]);

  
  /**
   * 半角文字を全角文字に変換する
   * @param {String} inputValue 変換対象の半角文字列
   * @return {String} 全角変換した文字列
   */
  const halfWidthToFullWidth = (inputValue: string) => {
    return inputValue.replace(/[A-Za-z0-9]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xFEE0);
    });
  }

  return (
    <Form.Group className="mb-2 row">
      <Form.Label className="col-lg-2 col-12 d-flex align-items-center">
        代表者
        <span className="text-small text-primary ms-3">※条件必須</span>
      </Form.Label>
      <div className="col-lg-10 col-12">
        <Form.Control
          type="text"
          value={representative.value}
          placeholder="法人の場合は、代表者名を入力してください。個人の場合は、入力不要です。"
          onChange={handleChange}
          aria-label="代表者"
          isInvalid={representative.errorMessage !== ''}
        />
        {representative.errorMessage !== '' ? <div className="text-end text-danger small">{representative.errorMessage}</div> : ''}
      </div>
    </Form.Group>
  );
};

export default RepresentativeForm;